import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { change_Current_Location, changeHeaderTitle } from '../store';
import axios from 'axios';
import { getBrand } from '../config/brand';
import { Banknote, ArrowRightLeft, RefreshCw, AlertCircle, CheckCircle, Info, Search } from 'lucide-react';
import { parseAccountHolderResponse } from './hook/useAccountHolderVerify';

function ProfitSettlement() {
  const dispatch = useDispatch();
  const MyData = useSelector((state) => state.MyData);
  const isFastBrand = getBrand().key === 'fast';
  // 예금주 조회는 fast 브랜드에서만 사용
  const useHolderVerification = isFastBrand;

  const [loading, setLoading] = useState(false);
  const [accountBalance, setAccountBalance] = useState(null);
  const [transferAmount, setTransferAmount] = useState('');
  const [targetAccount, setTargetAccount] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [selectedBankCode, setSelectedBankCode] = useState(''); // 기본값: 빈 값
  const [bankInfoList, setBankInfoList] = useState([]);
  const [transferHistory, setTransferHistory] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [otpToken, setOtpToken] = useState('');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [bankSearchText, setBankSearchText] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [rvAccountCntn, setRvAccountCntn] = useState('');
  const [holderLoading, setHolderLoading] = useState(false);
  const [holderVerified, setHolderVerified] = useState(false);

  useEffect(() => {
    dispatch(change_Current_Location("수익 정산"));
    dispatch(changeHeaderTitle({ mainTitle: "수익 정산", subTitle: "모계좌 자산 이체 관리" }));

    // 초기 데이터 로드
    loadAccountBalance();
    loadTransferHistory();
    fetchBankInfo();
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBankDropdown && !event.target.closest('.bank-search-container')) {
        setShowBankDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBankDropdown]);

  // 토스트 메시지 자동 사라짐
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000); // 5초 후 자동 사라짐

      return () => clearTimeout(timer);
    }
  }, [message.text]);

  // 은행 정보 조회
  const fetchBankInfo = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API + '/api/payout/bank-info',
        { withCredentials: true }
      );

      if (response.data.code === 1000) {
        setBankInfoList(response.data.data.bank_info || []);
      }
    } catch (error) {
      console.error('은행 정보 조회 오류:', error);
    }
  };

  // 계좌 잔액 조회
  const loadAccountBalance = async () => {
    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/balance", {}, {
        withCredentials: true,
      });

      if (response.data.code === 1000) {
        const data = response.data.data;
        // 기존 응답: data.result.balance_amount / 신규 응답: data.available_balance
        let balance = data.result
          ? data.result.balance_amount
          : Number(data.available_balance) || 0;

        // baekma: 내부계좌 잔고로 대체
        if (getBrand().key === "baekma") {
          try {
            const summaryRes = await axios.get(
              process.env.REACT_APP_API + "/api/master-balance/summary",
              { withCredentials: true }
            );
            if (summaryRes.data?.code < 2000) {
              balance = summaryRes.data.data?.balance ?? balance;
            }
          } catch (err) {
            console.error("내부계좌 잔고 조회 오류:", err);
          }
        }

        setAccountBalance({
          accountNumber: data.account || data.tmnId || '',
          bankCode: data.bank_code || '',
          totalBalance: balance,
          availableBalance: balance
        });
      } else {
        setMessage({ type: 'error', text: response.data.msg || '잔액 조회에 실패했습니다.' });
      }
    } catch (error) {
      console.error('잔액 조회 오류:', error);
      setMessage({ type: 'error', text: '잔액 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  // UUID 생성 (Idempotency-Key)
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // OTP 토큰 입력 핸들러
  const changeOtpToken = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value) && value.length <= 6) {
      setOtpToken(value);
    }
  };

  // 이체 실행 버튼 클릭 (OTP 모달 표시)
  const handleTransferClick = () => {
    if (!transferAmount || !targetAccount || !selectedBankCode) {
      setMessage({ type: 'error', text: '이체 금액, 은행 선택, 계좌번호를 모두 입력해주세요.' });
      return;
    }

    if (!accountHolder.trim()) {
      setMessage({ type: 'error', text: '예금주명을 입력해주세요.' });
      return;
    }

    if (useHolderVerification && !holderVerified) {
      setMessage({ type: 'error', text: '예금주 조회를 먼저 완료해주세요.' });
      return;
    }

    const amount = parseFloat(transferAmount);

    // 이체 금액 유효성 검증
    if (amount <= 0) {
      setMessage({ type: 'error', text: '이체 금액은 0원보다 커야 합니다.' });
      return;
    }

    if (amount > accountBalance?.availableBalance) {
      setMessage({ type: 'error', text: '이체 금액이 사용 가능한 잔액을 초과합니다.' });
      return;
    }

    // 모든 검증 통과 시 OTP 모달 표시
    setShowOtpModal(true);
    setOtpToken('');
  };

  // 실제 이체 실행 (OTP 모달에서 호출)
  const handleExecuteTransfer = async () => {
    if (!otpToken || otpToken.length !== 6) {
      setMessage({ type: 'error', text: 'OTP 코드를 입력해주세요. (6자리 숫자)' });
      return;
    }

    const amount = parseFloat(transferAmount);

    try {
      setLoading(true);
      const response = await axios.post(process.env.REACT_APP_API + "/api/transfer/company", {
        amount: amount,
        rvBankCode: selectedBankCode,
        rvAccount: targetAccount,
        transferReason: "월간 수익 정산",
        rvAccountCntn: rvAccountCntn,
        otpToken: otpToken
      }, {
        withCredentials: true,
        headers: {
          'Idempotency-Key': generateUUID()
        }
      });

      if (response.data.code < 2000) {
        setMessage({ type: 'success', text: '이체가 성공적으로 완료되었습니다.' });
        setTransferAmount('');
        setTargetAccount('');
        setAccountHolder('');
        setSelectedBankCode('');
        setBankSearchText('');
        setRvAccountCntn('');
        setOtpToken('');
        setShowOtpModal(false);
        setHolderVerified(false);
        // 데이터 새로고침
        loadAccountBalance();
        loadTransferHistory();
      } else {
        setMessage({ type: 'error', text: response.data.msg || response.data.message || '이체에 실패했습니다.' });
        setOtpToken('');
      }
    } catch (error) {
      console.error('이체 오류:', error);
      setMessage({ type: 'error', text: error.response?.data?.msg || error.response?.data?.message || '이체 중 오류가 발생했습니다.' });
      setOtpToken('');
    } finally {
      setLoading(false);
    }
  };

  // 이체 내역 조회
  const loadTransferHistory = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_API + "/api/transfer/company/logs", {
        withCredentials: true,
      });

      if (response.data.code < 2000) {
        setTransferHistory(response.data.data?.logs || []);
      }
    } catch (error) {
      console.error('이체 내역 조회 오류:', error);
    }
  };

  // 금액 포맷팅
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  // 날짜 포맷팅 (한국 시간으로 변환)
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // 서버에서 UTC 시간으로 오는 것을 명시적으로 처리
    const utcTime = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcTime).toLocaleString('ko-KR', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // 적요 입력 핸들러 (모든 문자 기준 10글자 제한)
  const handleRvAccountCntnChange = (e) => {
    const value = e.target.value;
    
    // 총 10글자까지만 입력 허용
    if (value.length <= 10) {
      setRvAccountCntn(value);
    } else {
      // 10글자를 초과하면 10글자까지만 잘라서 설정
      setRvAccountCntn(value.substring(0, 10));
    }
  };

  // 은행 코드를 은행명으로 변환
  const getBankName = (bankCode) => {
    const bank = bankInfoList.find(b => b.code === bankCode);
    return bank ? bank.bank_name : bankCode;
  };

  // 검색어로 필터링된 은행 목록
  const filteredBanks = bankInfoList.filter(bank => 
    bank.bank_name.toLowerCase().includes(bankSearchText.toLowerCase())
  );

  // 은행 선택 핸들러
  const handleBankSelect = (bankCode, bankName) => {
    setSelectedBankCode(bankCode);
    setBankSearchText(bankName);
    setShowBankDropdown(false);
    if (useHolderVerification) {
      setHolderVerified(false);
      setAccountHolder('');
    }
  };

  // 예금주 조회 (fast 브랜드 전용)
  const handleCheckAccountHolder = async () => {
    if (!selectedBankCode) {
      setMessage({ type: 'error', text: '은행을 먼저 선택해주세요.' });
      return;
    }
    if (!/^\d{10,20}$/.test(targetAccount)) {
      setMessage({ type: 'error', text: '계좌번호는 10-20자리 숫자여야 합니다.' });
      return;
    }
    setHolderLoading(true);
    try {
      const res = await axios.post(
        process.env.REACT_APP_API + "/api/transfer/account-holder",
        { bankCode: selectedBankCode, account: targetAccount },
        { withCredentials: true }
      );
      const result = parseAccountHolderResponse(res.data);
      if (result.ok) {
        setAccountHolder(result.depositor);
        setHolderVerified(true);
        setMessage({ type: 'success', text: `예금주 조회 완료: ${result.depositor}` });
      } else {
        setAccountHolder('');
        setHolderVerified(false);
        setMessage({ type: 'error', text: result.message });
      }
    } catch (err) {
      console.error('예금주 조회 오류:', err);
      setAccountHolder('');
      setHolderVerified(false);
      setMessage({ type: 'error', text: '예금주 조회 중 오류가 발생했습니다.' });
    } finally {
      setHolderLoading(false);
    }
  };

  // selectedBankCode가 변경될 때 bankSearchText 업데이트
  useEffect(() => {
    if (selectedBankCode && bankInfoList.length > 0) {
      const bank = bankInfoList.find(b => b.code === selectedBankCode);
      if (bank) {
        setBankSearchText(bank.bank_name);
      }
    } else if (!selectedBankCode) {
      setBankSearchText('');
    }
  }, [selectedBankCode, bankInfoList]);

  return (
    <div className="profit-settlement-container">
      <div className="page-header">
        <h1>수익 정산</h1>
        <p>모계좌(가상계좌) 자산을 실제 계좌로 이체합니다.</p>
      </div>

      {/* 토스트 메시지 */}
      {message.text && (
        <div className={`toast ${message.type === 'success' ? 'toast-success' : 'toast-error'}`}>
          <div className="toast-content">
            <div className="toast-icon">
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
            </div>
            <div className="toast-message">
              <div className="toast-title">
                {message.type === 'success' ? '성공' : '오류'}
              </div>
              <div className="toast-text">{message.text}</div>
            </div>
            <button
              className="toast-close"
              onClick={() => setMessage({ type: '', text: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 모계좌 정보 (컴팩트) */}
      <div className="account-info-compact">
        <div className="account-header">
          <div className="account-title">
            <Banknote size={20} />
            <span>모계좌 정보</span>
          </div>
          <button
            className="btn-refresh-small"
            onClick={loadAccountBalance}
            disabled={loading}
            title="잔액 새로고침"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        {accountBalance ? (
          <div className="account-details">
            <div className="account-number">
              <span className="label">계좌:</span>
              <span className="value">{accountBalance.accountNumber}</span>
            </div>
            <div className="account-number">
              <span className="label">은행:</span>
              <span className="value">{getBankName(accountBalance.bankCode)}</span>
            </div>
            <div className="balance-summary">
              <div className="balance-item">
                <span className="label">잔액:</span>
                <span className="value amount">{formatAmount(accountBalance.availableBalance)}원</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="loading-compact">잔액 정보를 불러오는 중...</div>
        )}
      </div>

      <div className="settlement-grid">

        {/* 이체 폼 */}
        <div className="card">
          <div className="card-header">
            <ArrowRightLeft size={24} />
            <h3>자산 이체</h3>
          </div>
          <div className="card-content">
            <div className="transfer-form">
              <div className="form-group">
                <label>이체 금액 (원)</label>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="이체할 금액을 입력하세요"
                  disabled={loading}
                />
              </div>

              <div className="form-group" style={{ position: 'relative' }}>
                <label>은행 선택</label>
                <div className="bank-search-container">
                  <input
                    type="text"
                    className="form-control bank-search-input"
                    placeholder="은행명을 검색하세요"
                    value={bankSearchText}
                    onChange={(e) => {
                      setBankSearchText(e.target.value);
                      setShowBankDropdown(true);
                    }}
                    onFocus={() => setShowBankDropdown(true)}
                    disabled={loading}
                    autoComplete="off"
                  />
                  {showBankDropdown && filteredBanks.length > 0 && (
                    <div className="bank-dropdown">
                      {filteredBanks.map(bank => (
                        <div
                          key={bank.code}
                          className={`bank-dropdown-item ${selectedBankCode === bank.code ? 'selected' : ''}`}
                          onClick={() => handleBankSelect(bank.code, bank.bank_name)}
                        >
                          {bank.bank_name}
                        </div>
                      ))}
                    </div>
                  )}
                  {showBankDropdown && filteredBanks.length === 0 && bankSearchText && (
                    <div className="bank-dropdown">
                      <div className="bank-dropdown-item no-results">검색 결과가 없습니다</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>이체받을 계좌번호</label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    value={targetAccount}
                    onChange={(e) => {
                      const cleanedValue = e.target.value.replace(/[^0-9]/g, '');
                      setTargetAccount(cleanedValue);
                      if (useHolderVerification) {
                        setHolderVerified(false);
                        setAccountHolder('');
                      }
                    }}
                    placeholder="계좌번호를 입력하세요"
                    disabled={loading}
                    style={{ flex: 1 }}
                  />
                  {useHolderVerification && (
                    <button
                      type="button"
                      onClick={handleCheckAccountHolder}
                      disabled={loading || holderLoading || !selectedBankCode || !targetAccount}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '0 12px',
                        fontSize: '13px',
                        borderRadius: '4px',
                        border: '1px solid #1e40af',
                        backgroundColor: holderVerified ? '#d1fae5' : '#1e40af',
                        color: holderVerified ? '#065f46' : '#ffffff',
                        cursor: (loading || holderLoading || !selectedBankCode || !targetAccount) ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {holderLoading ? '...' : holderVerified ? <><CheckCircle size={14} />확인됨</> : <><Search size={14} />예금주 조회</>}
                    </button>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>예금주명</label>
                <input
                  type="text"
                  value={accountHolder}
                  onChange={(e) => setAccountHolder(e.target.value)}
                  placeholder={useHolderVerification ? '계좌번호 옆 "예금주 조회" 버튼을 눌러주세요' : '예금주명을 입력하세요'}
                  disabled={loading}
                  readOnly={useHolderVerification}
                  style={useHolderVerification ? { backgroundColor: '#f9fafb', cursor: 'default' } : undefined}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  적요
                  <span className="tooltip-container">
                    <Info size={16} className="tooltip-icon" />
                    <span className="tooltip-text">해당 계좌에 찍히는 문구</span>
                  </span>
                </label>
                <input
                  type="text"
                  id="deposit_memo"
                  value={rvAccountCntn}
                  onChange={handleRvAccountCntnChange}
                  placeholder="적요를 입력하세요 (10글자 이내)"
                  disabled={loading}
                />
              </div>

              <button
                className="btn-transfer"
                onClick={handleTransferClick}
                disabled={loading || !transferAmount || !targetAccount || !accountHolder.trim() || !selectedBankCode}
              >
                {loading ? '처리 중...' : '이체 실행'}
              </button>
            </div>
          </div>
        </div>

        {/* 이체 내역 */}
        <div className="card">
          <div className="card-header">
            <h3>이체 내역</h3>
            <button
              className="btn-refresh"
              onClick={loadTransferHistory}
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
          </div>
          <div className="card-content">
            {transferHistory.length > 0 ? (
              <>
                {/* 데스크톱용 테이블 */}
                <div className="transfer-history desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>이체일시</th>
                        <th>이체금액</th>
                        <th>은행</th>
                        <th>계좌번호</th>
                        <th>적요</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferHistory.map((item, index) => (
                        <tr key={item.id || index}>
                          <td>{formatDate(item.created_at)}</td>
                          <td className="amount">{formatAmount(item.amount)}원</td>
                          <td>{getBankName(item.rv_bank_code)}</td>
                          <td>{item.rv_account}</td>
                          <td>{item.rv_account_cntn || '-'}</td>
                          <td>
                            <span className={`status ${
                              item.transfer_status === 'SUCCESS' ? 'status-success' :
                              item.transfer_status === 'PENDING' ? 'status-pending' :
                              item.transfer_status === 'FAILED' ? 'status-failed' : 'status-unknown'
                            }`}>
                              {item.transfer_status === 'SUCCESS' ? '성공' :
                               item.transfer_status === 'PENDING' ? '처리중' :
                               item.transfer_status === 'FAILED' ? '실패' : '알 수 없음'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일용 카드 리스트 */}
                <div className="transfer-cards mobile-only">
                  {transferHistory.map((item, index) => (
                    <div className="transfer-card" key={item.id || index}>
                      <div className="transfer-card-header">
                        <span className="transfer-card-date">{formatDate(item.created_at)}</span>
                        <span className={`status ${
                          item.transfer_status === 'SUCCESS' ? 'status-success' :
                          item.transfer_status === 'PENDING' ? 'status-pending' :
                          item.transfer_status === 'FAILED' ? 'status-failed' : 'status-unknown'
                        }`}>
                          {item.transfer_status === 'SUCCESS' ? '성공' :
                           item.transfer_status === 'PENDING' ? '처리중' :
                           item.transfer_status === 'FAILED' ? '실패' : '알 수 없음'}
                        </span>
                      </div>
                      <div className="transfer-card-amount">
                        {formatAmount(item.amount)}원
                      </div>
                      <div className="transfer-card-details">
                        <div className="transfer-card-row">
                          <span className="label">은행</span>
                          <span className="value">{getBankName(item.rv_bank_code)}</span>
                        </div>
                        <div className="transfer-card-row">
                          <span className="label">계좌</span>
                          <span className="value">{item.rv_account}</span>
                        </div>
                        <div className="transfer-card-row">
                          <span className="label">적요</span>
                          <span className="value">{item.rv_account_cntn || '-'}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="empty-state">이체 내역이 없습니다.</div>
            )}
          </div>
        </div>
      </div>

      {/* OTP 입력 모달 */}
      {showOtpModal && (
        <div className="otp-modal-overlay" onClick={() => setShowOtpModal(false)}>
          <div className="otp-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="otp-modal-header">
              <h3>OTP 인증</h3>
              <button
                className="otp-modal-close"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpToken('');
                }}
              >
                ×
              </button>
            </div>
            <div className="otp-modal-body">
              <p style={{ marginBottom: '20px', color: '#666' }}>
                이체를 완료하기 위해 OTP 코드를 입력해주세요.
              </p>
              <div className="form-group">
                <label>OTP 코드 (6자리)</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="000000"
                  maxLength={6}
                  value={otpToken}
                  onChange={changeOtpToken}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otpToken.length === 6 && !loading) {
                      handleExecuteTransfer();
                    }
                  }}
                  style={{ textAlign: "center", fontSize: "24px", letterSpacing: "8px", padding: "15px" }}
                  disabled={loading}
                  autoFocus
                />
              </div>
            </div>
            <div className="otp-modal-footer">
              <button
                className="btn-transfer"
                onClick={handleExecuteTransfer}
                disabled={loading || !otpToken || otpToken.length !== 6}
              >
                {loading ? '처리 중...' : '이체 실행'}
              </button>
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowOtpModal(false);
                  setOtpToken('');
                }}
                disabled={loading}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
                .profit-settlement-container {
                    padding: 20px;
                    margin: 0 auto;
                    margin-top: 80px; /* 헤더 높이만큼 여백 추가 */
                }

                .page-header {
                    margin-bottom: 30px;
                }

                .page-header h1 {
                    font-size: 28px;
                    font-weight: 600;
                    color: #1f2937;
                    margin-bottom: 8px;
                    text-align: left; /* 왼쪽 정렬 */
                }

                .page-header p {
                    color: #6b7280;
                    font-size: 16px;
                    text-align: left; /* 왼쪽 정렬 */
                }

                .toast {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 1000;
                    max-width: 400px;
                    min-width: 300px;
                    animation: slideInRight 0.3s ease-out;
                }

                .toast-content {
                    display: flex;
                    align-items: flex-start;
                    gap: 12px;
                    padding: 16px;
                    border-radius: 8px;
                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                    backdrop-filter: blur(10px);
                }

                .toast-success .toast-content {
                    background: rgba(16, 185, 129, 0.95);
                    color: white;
                    border: 1px solid rgba(16, 185, 129, 0.2);
                }

                .toast-error .toast-content {
                    background: rgba(239, 68, 68, 0.95);
                    color: white;
                    border: 1px solid rgba(239, 68, 68, 0.2);
                }

                .toast-icon {
                    flex-shrink: 0;
                    margin-top: 2px;
                }

                .toast-message {
                    flex: 1;
                }

                .toast-title {
                    font-weight: 600;
                    font-size: 14px;
                    margin-bottom: 4px;
                }

                .toast-text {
                    font-size: 13px;
                    opacity: 0.9;
                    line-height: 1.4;
                }

                .toast-close {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 18px;
                    font-weight: bold;
                    cursor: pointer;
                    padding: 0;
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: background-color 0.2s;
                    flex-shrink: 0;
                }

                .toast-close:hover {
                    background: rgba(255, 255, 255, 0.2);
                }

                @keyframes slideInRight {
                    from {
                        transform: translateX(100%);
                        opacity: 0;
                    }
                    to {
                        transform: translateX(0);
                        opacity: 1;
                    }
                }

                .account-info-compact {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 16px 20px;
                    margin-bottom: 24px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                }

                .account-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;
                }

                .account-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .btn-refresh-small {
                    background: #f3f4f6;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 6px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s;
                }

                .btn-refresh-small:hover {
                    background: #e5e7eb;
                }

                .btn-refresh-small:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .account-details {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 12px;
                }

                .account-number {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .account-number .label {
                    font-size: 14px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .account-number .value {
                    font-size: 14px;
                    color: #1f2937;
                    font-weight: 600;
                    font-family: 'Courier New', monospace;
                }

                .balance-summary {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    align-items: flex-start;
                }

                .balance-item {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .balance-item .label {
                    font-size: 13px;
                    color: #6b7280;
                    font-weight: 500;
                }

                .balance-item .value.amount {
                    font-size: 16px;
                    color: #059669;
                    font-weight: 700;
                }

                .loading-compact {
                    text-align: center;
                    color: #6b7280;
                    font-size: 14px;
                    padding: 8px 0;
                }

                .settlement-grid {
                    display: grid;
                    grid-template-columns: 35% 65%;
                    gap: 20px;
                    margin-bottom: 30px;
                }

                .card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .card-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 20px;
                    background: #f8fafc;
                    border-bottom: 1px solid #e2e8f0;
                }

                .card-header h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0;
                }

                .btn-refresh {
                    margin-left: auto;
                    background: none;
                    border: none;
                    padding: 8px;
                    border-radius: 6px;
                    cursor: pointer;
                    color: #6b7280;
                    transition: all 0.2s;
                }

                .btn-refresh:hover {
                    background: #e2e8f0;
                    color: #374151;
                }

                .btn-refresh:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                .card-content {
                    padding: 20px;
                }

                .balance-info {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                .balance-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .balance-item .label {
                    font-weight: 500;
                    color: #6b7280;
                }

                .balance-item .value {
                    font-weight: 600;
                    color: #1f2937;
                }

                .balance-item .amount {
                    font-size: 18px;
                    color: #059669;
                }

                .loading-state {
                    text-align: left; /* 왼쪽 정렬로 변경 */
                    color: #6b7280;
                    padding: 20px;
                }

                .transfer-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-group label {
                    font-weight: 500;
                    color: #374151;
                }

                .tooltip-container {
                    position: relative;
                    display: inline-flex;
                    align-items: center;
                }

                .tooltip-icon {
                    color: #6b7280;
                    cursor: help;
                    transition: color 0.2s;
                }

                .tooltip-icon:hover {
                    color: #3b82f6;
                }

                .tooltip-text {
                    visibility: hidden;
                    opacity: 0;
                    position: absolute;
                    bottom: 125%;
                    left: 0;
                    transform: translateX(0);
                    margin-left: 4px;
                    background-color: #1f2937;
                    color: white;
                    text-align: center;
                    padding: 6px 10px;
                    border-radius: 6px;
                    font-size: 12px;
                    white-space: nowrap;
                    z-index: 10000;
                    transition: opacity 0.2s, visibility 0.2s;
                    pointer-events: none;
                }

                .tooltip-text::after {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 8px;
                    transform: translateX(0);
                    border: 5px solid transparent;
                    border-top-color: #1f2937;
                }

                .tooltip-container:hover .tooltip-text {
                    visibility: visible;
                    opacity: 1;
                }

                .form-group input {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 16px;
                    transition: border-color 0.2s;
                }

                .form-group input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-group input:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                }

                .bank-search-container {
                    position: relative;
                }

                .bank-search-input {
                    padding: 12px;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    font-size: 16px;
                    transition: border-color 0.2s;
                }

                .bank-search-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .bank-search-input:disabled {
                    background-color: #f9fafb;
                    color: #6b7280;
                }

                .bank-dropdown {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: white;
                    border: 1px solid #d1d5db;
                    border-radius: 8px;
                    margin-top: 4px;
                    max-height: 200px;
                    overflow-y: auto;
                    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                    z-index: 1000;
                }

                .bank-dropdown-item {
                    padding: 12px;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    border-bottom: 1px solid #f3f4f6;
                }

                .bank-dropdown-item:last-child {
                    border-bottom: none;
                }

                .bank-dropdown-item:hover {
                    background-color: #f3f4f6;
                }

                .bank-dropdown-item.selected {
                    background-color: #eff6ff;
                    color: #2563eb;
                    font-weight: 600;
                }

                .bank-dropdown-item.no-results {
                    color: #6b7280;
                    cursor: default;
                    text-align: center;
                }

                .bank-dropdown-item.no-results:hover {
                    background-color: white;
                }

                .input-with-button {
                    display: flex;
                    gap: 8px;
                }

                .input-with-button input {
                    flex: 1;
                }

                .btn-check {
                    padding: 12px 16px;
                    background: #3b82f6;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    font-weight: 500;
                    transition: background-color 0.2s;
                }

                .btn-check:hover:not(:disabled) {
                    background: #2563eb;
                }

                .btn-check:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .account-holder {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 8px;
                    color: #166534;
                    font-weight: 500;
                }

                .text-success {
                    color: #16a34a;
                }

                .btn-transfer {
                    padding: 16px;
                    background: #059669;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-transfer:hover:not(:disabled) {
                    background: #047857;
                }

                .btn-transfer:disabled {
                    background: #9ca3af;
                    cursor: not-allowed;
                }

                .desktop-only {
                    display: block !important;
                }

                .mobile-only {
                    display: none !important;
                }

                .transfer-history table {
                    width: 100%;
                    border-collapse: collapse;
                }

                .transfer-history th,
                .transfer-history td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                }

                .transfer-history th {
                    background: #f9fafb;
                    font-weight: 600;
                    color: #374151;
                }

                .transfer-history .amount {
                    font-weight: 600;
                    color: #059669;
                }

                /* 모바일 카드 스타일 */
                .transfer-cards {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .transfer-card {
                    background: #f9fafb;
                    border-radius: 12px;
                    padding: 16px;
                    border: 1px solid #e5e7eb;
                }

                .transfer-card-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                }

                .transfer-card-date {
                    font-size: 13px;
                    color: #6b7280;
                }

                .transfer-card-amount {
                    font-size: 20px;
                    font-weight: 700;
                    color: #059669;
                    margin-bottom: 12px;
                }

                .transfer-card-details {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }

                .transfer-card-row {
                    display: flex;
                    justify-content: space-between;
                    font-size: 14px;
                }

                .transfer-card-row .label {
                    color: #6b7280;
                }

                .transfer-card-row .value {
                    color: #1f2937;
                    font-weight: 500;
                }

                .status {
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .status-success {
                    background: #d1fae5;
                    color: #065f46;
                }

                .status-pending {
                    background: #fef3c7;
                    color: #92400e;
                }

                .status-failed {
                    background: #fee2e2;
                    color: #991b1b;
                }

                .status-unknown {
                    background: #f3f4f6;
                    color: #6b7280;
                }

                .empty-state {
                    text-align: left; /* 왼쪽 정렬로 변경 */
                    color: #6b7280;
                    padding: 40px;
                }

                .otp-modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 10000;
                }

                .otp-modal-content {
                    background: white;
                    border-radius: 12px;
                    width: 90%;
                    max-width: 400px;
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
                    animation: slideIn 0.3s ease-out;
                }

                @keyframes slideIn {
                    from {
                        transform: translateY(-20px);
                        opacity: 0;
                    }
                    to {
                        transform: translateY(0);
                        opacity: 1;
                    }
                }

                .otp-modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 20px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .otp-modal-header h3 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 600;
                    color: #1f2937;
                }

                .otp-modal-close {
                    background: none;
                    border: none;
                    font-size: 28px;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 0;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: background-color 0.2s;
                }

                .otp-modal-close:hover {
                    background: #f3f4f6;
                }

                .otp-modal-body {
                    padding: 20px;
                }

                .otp-modal-footer {
                    display: flex;
                    gap: 10px;
                    padding: 20px;
                    border-top: 1px solid #e5e7eb;
                }

                .otp-modal-footer .btn-transfer {
                    flex: 1;
                    margin: 0;
                }

                .btn-cancel {
                    padding: 16px 24px;
                    background: #f3f4f6;
                    color: #374151;
                    border: none;
                    border-radius: 8px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }

                .btn-cancel:hover:not(:disabled) {
                    background: #e5e7eb;
                }

                .btn-cancel:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                @media (max-width: 768px) {
                    .account-details {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 12px;
                    }

                    .balance-summary {
                        flex-direction: column;
                        gap: 8px;
                        align-items: flex-start;
                    }

                    .settlement-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .profit-settlement-container {
                        padding: 16px;
                    }

                    .desktop-only {
                        display: none !important;
                    }

                    .mobile-only {
                        display: flex !important;
                        flex-direction: column;
                        gap: 12px;
                    }
                }
            `}</style>
    </div>
  );
}

export default ProfitSettlement;
